interface ErrorDetails {
  message: string;
  url: string;
  referrer: string;
  timestamp: string;
  userAgent: string;
  type: "broken_link" | "404" | "error";
}

/** No-op implementation for SSR where window is not defined. */
const noopErrorTracker = {
  trackError: (_error: ErrorDetails) => {},
  track404: (_url: string) => {},
  trackBrokenLink: (_url: string) => {},
};

class ErrorTracker {
  private static instance: ErrorTracker | null = null;
  private readonly BATCH_SIZE = 10;
  private errorQueue: ErrorDetails[] = [];

  private constructor() {
    if (typeof window === "undefined") return;

    window.addEventListener("error", this.handleError.bind(this));
    window.addEventListener(
      "unhandledrejection",
      this.handlePromiseError.bind(this),
    );

    setInterval(this.sendQueuedErrors.bind(this), 30000);
  }

  public static getInstance(): ErrorTracker | typeof noopErrorTracker {
    if (typeof window === "undefined") {
      return noopErrorTracker;
    }
    if (!ErrorTracker.instance) {
      ErrorTracker.instance = new ErrorTracker();
    }
    return ErrorTracker.instance;
  }

  private async sendQueuedErrors() {
    if (this.errorQueue.length === 0) return;

    const errors = this.errorQueue.splice(0, this.BATCH_SIZE);

    try {
      // In production, replace with your actual error reporting endpoint
      await fetch("/api/error-reporting", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ errors }),
      });
    } catch (error) {
      // If sending fails, add back to queue
      this.errorQueue.unshift(...errors);
      console.error("Failed to send error report:", error);
    }
  }

  private handleError(event: ErrorEvent) {
    this.trackError({
      message: event.message,
      url: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      type: "error",
    });
  }

  private handlePromiseError(event: PromiseRejectionEvent) {
    this.trackError({
      message: event.reason?.message || "Unhandled Promise Rejection",
      url: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      type: "error",
    });
  }

  public trackError(error: ErrorDetails) {
    this.errorQueue.push(error);

    // If we have enough errors, send them immediately
    if (this.errorQueue.length >= this.BATCH_SIZE) {
      this.sendQueuedErrors();
    }

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("Error tracked:", error);
    }
  }

  public track404(url: string) {
    this.trackError({
      message: `404 - Page Not Found: ${url}`,
      url,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      type: "404",
    });
  }

  public trackBrokenLink(url: string) {
    this.trackError({
      message: `Broken Link Detected: ${url}`,
      url,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      type: "broken_link",
    });
  }
}

export const errorTracker = ErrorTracker.getInstance();
